// import $ from 'jquery'
import _ from 'lodash'
import Vue from 'vue'
import { DefaultIconMap } from '../../core/KConstants'
import KUuid from '../../core/utils/KUuid'
import KIcon from '../icon'
import KButton from '../button'
import KQueueTransition from './queue-transition'
import { POSITION } from './constants'
import { isVNode, KNextZIndex } from '../../core/utils'
import { document } from 'ssr-window'

const getDefaultIcon = function (type) {
  return DefaultIconMap[type] || DefaultIconMap.primary
}

const config = {
  duration: 3000,
  queueSize: 7,
}

const contentPropValidate = function (value) {
  return typeof value === 'string' || typeof value === 'number' || isVNode(value)
}

const Message = Vue.extend({
  props: {
    id: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'light',
    },
    message: {
      validator(value) {
        return contentPropValidate(value)
      },
      default: '',
    },
    underline: {
      validator(value) {
        return contentPropValidate(value)
      },
      default: '',
    },
    html: {
      type: Boolean,
      default: false,
    },
    template: {
      // 自定义模板
      validator(value) {
        return contentPropValidate(value)
      },
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    duration: {
      // 自动关闭时间，如果 autoClose === false, 则 duration 无意义
      type: Number,
      default: config.duration, // million seconds
    },
    autoClose: {
      // 自动关闭，
      type: Boolean,
      default: true,
    },
    isNotification: {
      type: Boolean,
      default: false,
    },
    position: {
      // for notification only
      type: String,
      default: POSITION.topRight,
    },
  },
  data() {
    return {
      focus: false,
    }
  },
  computed: {
    cssNamespace() {
      return this.isNotification ? 'km-notification' : 'km-message'
    },
    css() {
      const message = {
        [this.cssNamespace]: true,
        [`${this.cssNamespace}--${this.type}`]: true,
      }
      if (this.template) {
        message['is-customize'] = true
      }
      return message
    },
  },
  created() {
    this.startTimer()
  },
  methods: {
    handleMouseEnter() {
      this.focus = true
      this.pauseTimer()
    },
    handleMouseLeave() {
      this.focus = false
      this.startTimer()
    },
    pauseTimer() {
      clearTimeout(this.timer)
      this.timer = null
    },
    startTimer() {
      if (!this.autoClose) {
        return
      }
      this.timer = setTimeout(() => {
        this.destroy()
      }, this.duration)
    },
    destroy() {
      this.$emit('destroy', this.id)
    },
  },
  render() {
    const iconProps = {
      attrs: {
        circle: true,
        icon: this.icon,
      },
    }

    if (this.template) {
      return (
        <div
          class={this.css}
          onMouseenter={this.handleMouseEnter}
          onMouseleave={this.handleMouseLeave}
        >
          {this.template}
        </div>
      )
    }

    return (
      <div
        class={this.css}
        onMouseenter={this.handleMouseEnter}
        onMouseleave={this.handleMouseLeave}
      >
        <KIcon class={this.cssNamespace + '__icon'} {...iconProps} />
        <div class={this.cssNamespace + '__content'}>
          <div
            v-show={this.message}
            class={this.cssNamespace + '__message'}
          >
            {this.html ? <span domPropsInnerHTML={this.message} /> : this.message}
          </div>
          <div
            v-show={this.underline}
            class={this.cssNamespace + '__underline'}
          >
            {this.html ? <span domPropsInnerHTML={this.underline} /> : this.underline}
          </div>
        </div>
        <KButton
          class="is-shrinkless"
          icon="times"
          // v-show={this.focus || !this.autoClose}
          v-show={!this.autoClose}
          circle={true}
          type={this.type}
          size="mini"
          onClick={this.destroy}
        />
      </div>
    )
  },
})

const MessageManager = Vue.extend({
  components: {},
  data() {
    return {
      queue: [],
      isNotification: false,
      position: POSITION.topCenter,
    }
  },
  computed: {
    wrapperCss() {
      if (!this.isNotification) {
        return 'km-message__wrapper'
      }

      return {
        'km-notification__wrapper': true,
        [`km-notification__wrapper--${_.kebabCase(this.position)}`]: true,
      }
    },
    innerCss() {
      return this.isNotification ? 'km-notification__inner' : 'km-message__inner'
    },
  },
  methods: {
    removeAll() {
      setTimeout(() => {
        this.queue.shift()
        if (this.queue.length) {
          this.removeAll()
        }
      }, 100)
    },
    removeMessage(id) {
      const index = _.findIndex(this.queue, ['id', id])
      if (index > -1) {
        this.queue.splice(index, 1)
      }
    },
    addMessage(options) {
      if (typeof options === 'string' || isVNode(options)) {
        options = {
          message: options,
        }
      }

      if (!this.isNotification && this.queue.length >= config.queueSize) {
        this.queue.shift()
      }

      const id = KUuid()

      this.queue.push(
        _.assign(options, {
          icon: options.icon || getDefaultIcon(options.type),
          id,
        }),
      )
      this.$el.style.zIndex = KNextZIndex()

      return id
    },
  },
  render() {
    const queueProps = {
      props: {
        reverse: !this.isNotification,
        position: this.position,
      },
    }

    return (
      <div class={this.wrapperCss}>
        <KQueueTransition class={this.innerCss} {...queueProps}>
          {_.map(this.queue, (message) => {
            const props = {
              props: message,
            }
            return <Message
              onDestroy={this.removeMessage}
              key={message.id} {...props} />
          })}
        </KQueueTransition>
      </div>
    )
  },
})

let messageManager

const notificationManagerMap = {}

// 全局设置 Message
export const setMessageConfig = (customConfig) => {
  _.assign(config, customConfig)
}

export default {
  name: 'KMessage',
  message(options) {
    if (options.action && options.action === 'clear') {
      messageManager && messageManager.removeAll()
      return
    }

    if (messageManager === undefined) {
      /* eslint-disable-next-line */
      messageManager = new MessageManager({
        el: document.createElement('div'),
      })
      document.body.appendChild(messageManager.$el)
    }

    return messageManager.addMessage(options)
  },
  notification(options) {
    if (options.action && options.action === 'clear') {
      _.forEach(_.values(notificationManagerMap), (manager) => {
        manager.removeAll()
      })
      return
    }

    let { position } = options
    position = position || POSITION.topRight

    let manager = notificationManagerMap[position]
    if (manager === undefined) {
      notificationManagerMap[position] = manager = new MessageManager({
        el: document.createElement('div'),
        data() {
          return {
            position,
            isNotification: true,
          }
        },
      })
      document.body.appendChild(manager.$el)
    }

    manager.addMessage(
      _.assign(
        {
          isNotification: true,
          duration: 5000,
        },
        options,
      ),
    )
  },
}
